import { Button, Space, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useGetTemplatesQuery } from '@/state/slices/reports';
import { useDeleteTemplateMutation } from '@/state/slices/reportTemplates';
import type { Template } from '@/models/template';
import HeartLoader from '@/shared/ui/HeartLoader';
import { useConfirmDelete } from '../hooks/useConfirmDelete';
import { TemplateListToolbar } from '../TemplateListToolbar';
import { formatDate } from '../utils/formatDate';
import styles from './TemplateList.module.css';

const { Text } = Typography;

export function TemplateList() {
  const navigate = useNavigate();
  const { data, isLoading } = useGetTemplatesQuery();
  const [deleteTemplate] = useDeleteTemplateMutation();
  const templates = data?.results ?? [];

  const handleDelete = useConfirmDelete('Report Template', deleteTemplate);

  const columns: ColumnsType<Template> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => <Text strong>{name}</Text>,
    },
    {
      title: 'Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 200,
      render: formatDate,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => navigate(`/templates/templates/${record.uuid}`)}
          >
            Edit
          </Button>
          <Button
            type="link"
            danger
            aria-label="Delete"
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          />
        </Space>
      ),
    },
  ];

  return (
    <div className={styles.container}>
      <TemplateListToolbar kind="report" count={data?.count} />
      <Table
        rowKey="uuid"
        columns={columns}
        dataSource={templates}
        loading={{
          spinning: isLoading,
          indicator: <HeartLoader size="sm" />,
        }}
        pagination={false}
        locale={{ emptyText: 'No templates found' }}
      />
    </div>
  );
}
